import {useEffect, useState} from 'react'
import {
  desktopDownloadMetadataUrl,
  desktopReleasesOrigin,
  downloadUrlFromMetadata,
  type DesktopReleasePlatform
} from 'notability-services-common-universal'

/**
 * The releases-bucket key for a `Mac` / `Windows` product label, and `undefined` for anything else.
 *
 * Callers pass `undefined` whenever their surface isn't rendering — `useDesktopDownloadUrl` fetches
 * on mount rather than on click, so a hook call left outside the surface's own gate reaches the
 * bucket for every visitor on those platforms no matter what the gate decides.
 */
export function desktopReleasePlatform(label: 'Mac' | 'Windows' | undefined): DesktopReleasePlatform | undefined {
  return label === 'Mac' ? 'mac' : label === 'Windows' ? 'windows' : undefined
}

/**
 * The current download URL for one desktop platform, read from this environment's releases bucket in
 * the browser. `undefined` until it resolves, and for good if it can't — callers fall back to the
 * `/apps` page, which resolves the same metadata server-side.
 *
 * Pass `undefined` for a visitor with no desktop build to offer (a phone, say), and whenever the
 * calling surface is hidden, to skip the request.
 *
 * The bucket comes from the page's own origin, so each environment reads the builds it published and
 * `localhost` resolves nothing. Reading it from script depends on the `cors` block on the releases
 * buckets, defined in the `storage_buckets` and `storage_buckets_stage` Terraform modules; without it
 * the browser blocks the response and every caller falls back.
 *
 * A failure is deliberately not logged. The fallback is a working link to the download page, and this
 * runs on a dismissible upsell, so a console error on every render in a degraded environment would
 * cost more than it tells us — the server-side path in marketing is where these reads are logged.
 */
export function useDesktopDownloadUrl(platform: DesktopReleasePlatform | undefined): string | undefined {
  const [url, setUrl] = useState<string>()

  useEffect(() => {
    setUrl(undefined)
    if (platform === undefined) {
      return
    }
    const releasesOrigin = desktopReleasesOrigin(window.location.origin)
    if (releasesOrigin === undefined) {
      return
    }

    let active = true
    const load = async () => {
      try {
        const response = await fetch(desktopDownloadMetadataUrl(releasesOrigin, platform))
        if (!response.ok) {
          return
        }
        const resolved = downloadUrlFromMetadata(platform, releasesOrigin, await response.json())
        if (active) {
          setUrl(resolved)
        }
      } catch {
        // Left unresolved; the caller links to the download page instead.
      }
    }
    void load()

    return () => {
      active = false
    }
  }, [platform])

  return url
}
